import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { ROLES } from "../../config/roles";
import { staffInvitationService } from "../../services/api";

const getStaffLabelFromInvite = (invite) => {
  if (!invite) return "this staff member";

  const metadata = invite.metadata || invite.meta || {};

  const primaryName = [
    invite.staff_full_name,
    invite.staff_name,
    metadata.staff_full_name,
    metadata.staff_name,
  ].find((value) => typeof value === "string" && value.trim());

  if (primaryName) {
    return primaryName;
  }

  const firstName =
    invite.staff_first_name ||
    metadata.staff_first_name ||
    metadata.first_name ||
    invite.first_name;
  const lastName =
    invite.staff_last_name ||
    metadata.staff_last_name ||
    metadata.last_name ||
    invite.last_name;

  const combinedName = [firstName, lastName]
    .filter((value) => typeof value === "string" && value.trim())
    .join(" ");

  if (combinedName) {
    return combinedName;
  }

  const emailCandidate =
    invite.staff_email ||
    metadata.staff_email ||
    metadata.email ||
    invite.email;

  if (emailCandidate) {
    return emailCandidate;
  }

  if (invite.id) {
    return `invite #${invite.id}`;
  }

  return "this staff member";
};

const formatRoleLabel = (role) => {
  if (!role || typeof role !== "string") return null;
  return role
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getRoleLabelFromInvite = (invite) => {
  if (!invite) return null;
  const metadata = invite.metadata || invite.meta || {};
  const roleCandidate =
    invite.staff_role ||
    invite.role_type ||
    metadata.staff_role ||
    metadata.role_type ||
    metadata.role;
  return formatRoleLabel(roleCandidate);
};

const buildInviteDescription = (invite) => {
  const staffLabel = getStaffLabelFromInvite(invite);
  const roleLabel = getRoleLabelFromInvite(invite);
  if (roleLabel) {
    return `${staffLabel} requested to join your club as ${roleLabel}.`;
  }
  return `${staffLabel} requested to join your club.`;
};

const ConfirmActionModal = ({
  action,
  onCancel,
  onConfirm,
  loading,
  description,
}) => {
  const isAccept = action === "accept";
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-[24px] w-full max-w-2xl p-10 shadow-2xl">
        <h3 className="text-xl leading-8 font-bold text-[#1A212F] text-center mb-4">
          {`Are you sure you want to ${isAccept ? "accept" : "decline"} this request?`}
        </h3>
        <p className="text-sm text-gray-600 text-center mb-8">{description}</p>
        <div className="flex items-center justify-center gap-6">
          <button
            onClick={onCancel}
            disabled={loading}
            className="py-2 w-1/2 rounded-lg border border-[#E6E6E6] bg-white text-[#1E1E1E] text-sm shadow-sm disabled:opacity-50"
            style={{
              boxShadow: "0px 1px 5px 0px #00000014",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`${
              isAccept
                ? "bg-[#2283FF] hover:brightness-105"
                : "bg-[#FF3B30] hover:brightness-105 "
            } py-2 w-1/2 rounded-lg text-[#F5F5F5] text-sm disabled:opacity-50`}
            style={{
              boxShadow: "0px 1px 5px 0px #00000014",
            }}
          >
            {loading
              ? isAccept
                ? "Accepting..."
                : "Declining..."
              : isAccept
              ? "Accept Request"
              : "Decline Request"}
          </button>
        </div>
      </div>
    </div>
  );
};

const MultipleInvitesModal = ({
  invites,
  onClose,
  onActionClick,
  respondingMap,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Pending Staff Requests
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {invites.map((invite) => (
            <div
              key={invite.id}
              className="flex items-center justify-between px-6 py-3 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl shadow-lg"
              style={{ boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)" }}
            >
              <div className="flex items-center gap-2">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="24" height="24" rx="6" fill="url(#grad-club)" />
                  <defs>
                    <linearGradient
                      id="grad-club"
                      x1="12"
                      y1="0"
                      x2="12"
                      y2="24"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#DBA948" />
                      <stop offset="0.0001" stopColor="#FFC46B" />
                      <stop offset="1" stopColor="#FFA318" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="text-sm font-medium">
                  {buildInviteDescription(invite)}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => onActionClick(invite, "decline")}
                  disabled={Boolean(respondingMap[invite.id])}
                  className="bg-[#FF3B30] px-4 py-2 rounded-xl text-white font-bold disabled:opacity-50"
                >
                  Decline
                </button>
                <button
                  onClick={() => onActionClick(invite, "accept")}
                  disabled={Boolean(respondingMap[invite.id])}
                  className="bg-[#2283FF] px-4 py-2 rounded-xl text-white font-bold disabled:opacity-50"
                >
                  Accept
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ClubPendingStaffInvites = () => {
  const { currentRole } = useSelector((state) => state.auth);
  const [invites, setInvites] = useState([]);
  const [showMultipleModal, setShowMultipleModal] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, invite: null, action: null });
  const [respondingMap, setRespondingMap] = useState({});

  useEffect(() => {
    let isMounted = true;

    const fetchInvites = async () => {
      if (currentRole !== ROLES.ClubAdmin) {
        if (isMounted) {
          setInvites([]);
        }
        return;
      }

      try {
        const res = await staffInvitationService.getPendingStaffInvitations();
        if (!isMounted) return;

        if (res.success) {
          const data = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.results)
            ? res.data.results
            : [];
          setInvites(data);
        } else if (res.error) {
          toast.error(res.error);
        }
      } catch (error) {
        if (isMounted) {
          toast.error("Failed to load staff requests");
        }
      }
    };

    fetchInvites();

    return () => {
      isMounted = false;
    };
  }, [currentRole]);

  const handleOpenConfirm = (invite, action) => {
    setConfirm({ open: true, invite, action });
  };

  const handleConfirm = async () => {
    if (!confirm.invite || !confirm.action) return;

    const inviteId = confirm.invite.id;
    if (!inviteId) {
      setConfirm({ open: false, invite: null, action: null });
      return;
    }

    setRespondingMap((prev) => ({ ...prev, [inviteId]: true }));

    try {
      const res = await staffInvitationService.respondToStaffInvitation(
        inviteId,
        confirm.action === "accept" ? "accept" : "decline"
      );

      if (res.success) {
        setInvites((prev) => {
          const updated = prev.filter((invite) => invite.id !== inviteId);
          if (updated.length === 0) {
            setShowMultipleModal(false);
          }
          return updated;
        });
        toast.success(
          confirm.action === "accept"
            ? "Staff request accepted"
            : "Staff request declined"
        );
      } else {
        toast.error(res.error || "Failed to respond to request");
      }
    } catch (error) {
      toast.error("Failed to respond to request");
    } finally {
      setRespondingMap((prev) => {
        const updated = { ...prev };
        delete updated[inviteId];
        return updated;
      });
      setConfirm({ open: false, invite: null, action: null });
    }
  };

  if (currentRole !== ROLES.ClubAdmin || invites.length === 0) {
    return null;
  }

  const shouldShowStacked = invites.length > 1;

  return (
    <div className="mb-6 mt-4">
      {shouldShowStacked && (
        <div className="relative h-20 mt-32 md:-mt-2">
          {invites.slice(0, 3).map((invite, index) => (
            <div
              key={invite.id}
              className="absolute flex flex-col md:flex-row items-start md:items-center justify-between p-4 md:px-6 md:py-3 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl shadow-lg cursor-pointer w-[calc(100%-2rem)] md:w-full"
              style={{
                maxWidth: `${1400 - index * 24}px`,
                height: "auto",
                left: "1rem",
                bottom: `${20 - index * 10}px`,
                zIndex: invites.length - index,
                opacity: index === 0 ? 1 : index === 1 ? 0.7 : 0.5,
                boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)",
              }}
              onClick={() => setShowMultipleModal(true)}
            >
              <div className="flex items-center gap-2 mb-2 md:mb-0">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    width="24"
                    height="24"
                    rx="6"
                    fill="url(#paint0_linear_club)"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_club"
                      x1="12"
                      y1="0"
                      x2="12"
                      y2="24"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#DBA948" />
                      <stop offset="0.0001" stopColor="#FFC46B" />
                      <stop offset="1" stopColor="#FFA318" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="text-sm font-medium">
                  {buildInviteDescription(invite)}
                </span>
              </div>

              <div className="flex items-center gap-2 w-full md:w-auto">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenConfirm(invite, "decline");
                  }}
                  className="bg-[#FF3B30] px-4 py-2 rounded-xl text-white font-bold w-full md:w-auto"
                  disabled={Boolean(respondingMap[invite.id])}
                >
                  Decline
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenConfirm(invite, "accept");
                  }}
                  className="bg-[#2283FF] px-4 py-2 rounded-xl text-white font-bold w-full md:w-auto"
                  disabled={Boolean(respondingMap[invite.id])}
                >
                  Accept
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!shouldShowStacked && invites.length === 1 && (
        <div className="relative h-auto md:h-20 mt-32 md:-mt-2">
          <div
            className="absolute flex flex-col md:flex-row items-start md:items-center justify-between p-4 md:px-6 md:py-3 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl shadow-lg w-[calc(100%-2rem)] md:w-full"
            style={{
              maxWidth: "1400px",
              height: "auto",
              left: "1rem",
              bottom: "20px",
              boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)",
            }}
          >
            <div className="flex items-center gap-2 mb-2 md:mb-0">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="24"
                  height="24"
                  rx="6"
                  fill="url(#paint0_linear_club_single)"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_club_single"
                    x1="12"
                    y1="0"
                    x2="12"
                    y2="24"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#DBA948" />
                    <stop offset="0.0001" stopColor="#FFC46B" />
                    <stop offset="1" stopColor="#FFA318" />
                  </linearGradient>
                </defs>
              </svg>
              <span className="text-sm font-medium">
                {buildInviteDescription(invites[0])}
              </span>
            </div>

            <div className="flex items-center gap-2 w-full md:w-auto">
              <button
                onClick={() => handleOpenConfirm(invites[0], "decline")}
                className="bg-[#FF3B30] px-4 py-2 rounded-xl text-white font-bold w-full md:w-auto"
                disabled={Boolean(respondingMap[invites[0].id])}
              >
                Decline
              </button>
              <button
                onClick={() => handleOpenConfirm(invites[0], "accept")}
                className="bg-[#2283FF] px-4 py-2 rounded-xl text-white font-bold w-full md:w-auto"
                disabled={Boolean(respondingMap[invites[0].id])}
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {showMultipleModal && (
        <MultipleInvitesModal
          invites={invites}
          onClose={() => setShowMultipleModal(false)}
          onActionClick={(invite, action) => handleOpenConfirm(invite, action)}
          respondingMap={respondingMap}
        />
      )}

      {confirm.open && confirm.invite && (
        <ConfirmActionModal
          action={confirm.action}
          loading={Boolean(respondingMap[confirm.invite.id])}
          onCancel={() => setConfirm({ open: false, invite: null, action: null })}
          onConfirm={handleConfirm}
          description={buildInviteDescription(confirm.invite)}
        />
      )}
    </div>
  );
};

export default ClubPendingStaffInvites;
